import React, { type FC, useMemo } from "react";
import { ScrollShadow } from "@heroui/react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { Lang } from "@domain/intl";
import { useTranslation } from "@domain/intl/useTranslation";

/**
 * Renders one legal document from Markdown, picking the language version.
 *
 * The texts live as Markdown next to this file rather than as JSX because they
 * are maintained as documents: the same sources are reviewed, diffed and
 * published as PDF from `docs/gdpr/`. Hand-converting them to JSX would
 * guarantee drift between what a customer reads and what the record says.
 *
 * German is the authoritative version. Any locale without its own translation
 * falls back to English, and English falls back to German, so a document is
 * never blank.
 */
export interface LegalDocumentProps {
    /** Markdown source per language. `de` is mandatory - it is authoritative. */
    sources: { de: string; en?: string };
}

export const markdownComponents = {
    h1: ({ children, ...props }: React.ComponentProps<"h1">) => (
        <h1 className="mb-6 text-3xl font-bold" {...props}>
            {children}
        </h1>
    ),
    h2: ({ children, ...props }: React.ComponentProps<"h2">) => (
        <h2 className="mt-10 mb-3 border-b border-default/40 pb-1 text-xl font-semibold" {...props}>
            {children}
        </h2>
    ),
    h3: ({ children, ...props }: React.ComponentProps<"h3">) => (
        <h3 className="mt-6 mb-2 text-lg font-semibold" {...props}>
            {children}
        </h3>
    ),
    p: (props: React.ComponentProps<"p">) => <p className="my-3 leading-relaxed" {...props} />,
    ul: (props: React.ComponentProps<"ul">) => <ul className="my-3 list-disc space-y-1 pl-6" {...props} />,
    ol: (props: React.ComponentProps<"ol">) => <ol className="my-3 list-decimal space-y-1 pl-6" {...props} />,
    a: ({ children, ...props }: React.ComponentProps<"a">) => (
        <a className="text-accent underline underline-offset-2" target="_blank" rel="noopener noreferrer" {...props}>
            {children}
        </a>
    ),
    blockquote: (props: React.ComponentProps<"blockquote">) => (
        <blockquote className="my-4 border-l-4 border-accent/40 bg-default/20 py-2 pl-4 italic" {...props} />
    ),
    code: (props: React.ComponentProps<"code">) => (
        <code className="rounded bg-default/40 px-1 py-0.5 font-mono text-[0.9em]" {...props} />
    ),
    hr: (props: React.ComponentProps<"hr">) => <hr className="my-8 border-default/40" {...props} />,
    // Tables carry the storage inventory and the sub-processor list, which are
    // wide. Scroll the table, never the page.
    table: (props: React.ComponentProps<"table">) => (
        <div className="my-5 overflow-x-auto">
            <table className="w-full border-collapse text-sm" {...props} />
        </div>
    ),
    th: (props: React.ComponentProps<"th">) => (
        <th className="border border-default/50 bg-default/30 px-3 py-2 text-left font-semibold" {...props} />
    ),
    td: (props: React.ComponentProps<"td">) => (
        <td className="border border-default/50 px-3 py-2 align-top" {...props} />
    ),
};

export const LegalDocumentView: FC<LegalDocumentProps> = ({ sources }) => {
    const { locale } = useTranslation();

    const plugins = useMemo(() => [remarkGfm], []);

    const markdown = useMemo(() => {
        if (locale === Lang.De) {
            return sources.de;
        }
        return sources.en ?? sources.de;
    }, [locale, sources]);

    // AppLayout wraps every route in `h-full overflow-hidden`, so a long document
    // is simply cut off - the AVV runs to well over a thousand lines. The scroll
    // container has to live here rather than in the layout, which is deliberately
    // non-scrolling so the AG Grid pages can size themselves.
    return (
        <ScrollShadow className="h-full">
            <div className="mx-auto max-w-3xl px-5 py-8">
                <ReactMarkdown remarkPlugins={plugins} components={markdownComponents}>
                    {markdown}
                </ReactMarkdown>
            </div>
        </ScrollShadow>
    );
};
